import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { common as frCommon } from './local/fr/common';
import { common as enCommon } from './local/en/common';
import { common as esCommon } from './local/es/common';
import { common as itCommon } from './local/it/common';
import { common as nlCommon } from './local/nl/common';
import { common as deCommon } from './local/de/common';
import { common as ptCommon } from './local/pt/common';
import { common as elCommon } from './local/el/common';
import { common as plCommon } from './local/pl/common';
import { calendar as frCalendar } from './local/fr/calendar';
import { calendar as enCalendar } from './local/en/calendar';
import { calendar as esCalendar } from './local/es/calendar';
import { calendar as itCalendar } from './local/it/calendar';
import { calendar as nlCalendar } from './local/nl/calendar';
import { calendar as deCalendar } from './local/de/calendar';
import { calendar as ptCalendar } from './local/pt/calendar';
import { calendar as elCalendar } from './local/el/calendar';
import { calendar as plCalendar } from './local/pl/calendar';
import { explore as frExplore } from './local/fr/explore';
import { explore as enExplore } from './local/en/explore';
import { explore as esExplore } from './local/es/explore';
import { explore as itExplore } from './local/it/explore';
import { explore as nlExplore } from './local/nl/explore';
import { explore as deExplore } from './local/de/explore';
import { explore as ptExplore } from './local/pt/explore';
import { explore as elExplore } from './local/el/explore';
import { explore as plExplore } from './local/pl/explore';
import { howItWorks as frHowItWorks } from './local/fr/howItWorks';
import { howItWorks as enHowItWorks } from './local/en/howItWorks';
import { howItWorks as esHowItWorks } from './local/es/howItWorks';
import { howItWorks as itHowItWorks } from './local/it/howItWorks';
import { howItWorks as nlHowItWorks } from './local/nl/howItWorks';
import { howItWorks as deHowItWorks } from './local/de/howItWorks';
import { howItWorks as ptHowItWorks } from './local/pt/howItWorks';
import { howItWorks as elHowItWorks } from './local/el/howItWorks';
import { howItWorks as plHowItWorks } from './local/pl/howItWorks';
import { incomingRequests as frIncomingRequests } from './local/fr/incomingRequests';
import { incomingRequests as enIncomingRequests } from './local/en/incomingRequests';
import { incomingRequests as esIncomingRequests } from './local/es/incomingRequests';
import { incomingRequests as itIncomingRequests } from './local/it/incomingRequests';
import { incomingRequests as nlIncomingRequests } from './local/nl/incomingRequests';
import { incomingRequests as deIncomingRequests } from './local/de/incomingRequests';
import { incomingRequests as ptIncomingRequests } from './local/pt/incomingRequests';
import { incomingRequests as elIncomingRequests } from './local/el/incomingRequests';
import { incomingRequests as plIncomingRequests } from './local/pl/incomingRequests';
import { corporate as frCorporate } from './local/fr/corporate';
import { corporate as enCorporate } from './local/en/corporate';
import { corporate as esCorporate } from './local/es/corporate';
import { corporate as itCorporate } from './local/it/corporate';
import { corporate as nlCorporate } from './local/nl/corporate';
import { corporate as deCorporate } from './local/de/corporate';
import { corporate as ptCorporate } from './local/pt/corporate';
import { corporate as elCorporate } from './local/el/corporate';
import { corporate as plCorporate } from './local/pl/corporate';
import { terms as frTerms } from './local/fr/terms';
import { terms as enTerms } from './local/en/terms';
import { terms as esTerms } from './local/es/terms';
import { terms as itTerms } from './local/it/terms';
import { terms as nlTerms } from './local/nl/terms';
import { terms as deTerms } from './local/de/terms';
import { terms as ptTerms } from './local/pt/terms';
import { terms as elTerms } from './local/el/terms';
import { terms as plTerms } from './local/pl/terms';
import { privacy as frPrivacy } from './local/fr/privacy';
import { privacy as enPrivacy } from './local/en/privacy';
import { privacy as esPrivacy } from './local/es/privacy';
import { privacy as itPrivacy } from './local/it/privacy';
import { privacy as nlPrivacy } from './local/nl/privacy';
import { privacy as dePrivacy } from './local/de/privacy';
import { privacy as ptPrivacy } from './local/pt/privacy';
import { privacy as elPrivacy } from './local/el/privacy';
import { privacy as plPrivacy } from './local/pl/privacy';
import { destinations as frDestinations } from './local/fr/destinations';
import { destinations as enDestinations } from './local/en/destinations';
import { destinations as esDestinations } from './local/es/destinations';
import { destinations as itDestinations } from './local/it/destinations';
import { destinations as nlDestinations } from './local/nl/destinations';
import { destinations as deDestinations } from './local/de/destinations';
import { destinations as ptDestinations } from './local/pt/destinations';
import { destinations as elDestinations } from './local/el/destinations';
import { destinations as plDestinations } from './local/pl/destinations';

type TranslationNode = Record<string, any> | string | undefined;

function normalizeValueForComparison(value: string): string {
  return value
    .normalize('NFKC')
    .replace(/[’‘`´]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function shouldUseEnglishFallback(localeValue: string, frValue: string | undefined): boolean {
  if (!localeValue.trim()) return true;
  if (!frValue) return false;
  return normalizeValueForComparison(localeValue) === normalizeValueForComparison(frValue);
}

function applyEnglishFallback(
  localeNode: TranslationNode,
  frNode: TranslationNode,
  enNode: TranslationNode
): TranslationNode {
  if (localeNode === undefined) {
    return enNode ?? frNode;
  }

  if (typeof localeNode === 'string') {
    if (
      shouldUseEnglishFallback(localeNode, typeof frNode === 'string' ? frNode : undefined) &&
      typeof enNode === 'string' &&
      enNode.trim()
    ) {
      return enNode;
    }
    return localeNode;
  }

  if (localeNode && typeof localeNode === 'object') {
    const result: Record<string, any> = {};

    const localeKeys = Object.keys(localeNode);
    const frKeys = frNode && typeof frNode === 'object' ? Object.keys(frNode) : [];
    const enKeys = enNode && typeof enNode === 'object' ? Object.keys(enNode) : [];
    const keys = new Set([...localeKeys, ...frKeys, ...enKeys]);

    for (const key of keys) {
      const localeValue = (localeNode as Record<string, any>)[key];
      const frValue = frNode && typeof frNode === 'object' ? (frNode as Record<string, any>)[key] : undefined;
      const enValue = enNode && typeof enNode === 'object' ? (enNode as Record<string, any>)[key] : undefined;

      result[key] = applyEnglishFallback(localeValue, frValue, enValue);
    }

    return result;
  }

  return localeNode;
}

const baseResources = {
  fr: {
    common: frCommon,
    calendar: frCalendar,
    explore: frExplore,
    howItWorks: frHowItWorks,
    incomingRequests: frIncomingRequests,
    corporate: frCorporate,
    terms: frTerms,
    privacy: frPrivacy,
    destinations: frDestinations
  },
  en: {
    common: enCommon,
    calendar: enCalendar,
    explore: enExplore,
    howItWorks: enHowItWorks,
    incomingRequests: enIncomingRequests,
    corporate: enCorporate,
    terms: enTerms,
    privacy: enPrivacy,
    destinations: enDestinations
  },
  es: {
    common: esCommon,
    calendar: esCalendar,
    explore: esExplore,
    howItWorks: esHowItWorks,
    incomingRequests: esIncomingRequests,
    corporate: esCorporate,
    terms: esTerms,
    privacy: esPrivacy,
    destinations: esDestinations
  },
  it: {
    common: itCommon,
    calendar: itCalendar,
    explore: itExplore,
    howItWorks: itHowItWorks,
    incomingRequests: itIncomingRequests,
    corporate: itCorporate,
    terms: itTerms,
    privacy: itPrivacy,
    destinations: itDestinations
  },
  nl: {
    common: nlCommon,
    calendar: nlCalendar,
    explore: nlExplore,
    howItWorks: nlHowItWorks,
    incomingRequests: nlIncomingRequests,
    corporate: nlCorporate,
    terms: nlTerms,
    privacy: nlPrivacy,
    destinations: nlDestinations
  },
  de: {
    common: deCommon,
    calendar: deCalendar,
    explore: deExplore,
    howItWorks: deHowItWorks,
    incomingRequests: deIncomingRequests,
    corporate: deCorporate,
    terms: deTerms,
    privacy: dePrivacy,
    destinations: deDestinations
  },
  pt: {
    common: ptCommon,
    calendar: ptCalendar,
    explore: ptExplore,
    howItWorks: ptHowItWorks,
    incomingRequests: ptIncomingRequests,
    corporate: ptCorporate,
    terms: ptTerms,
    privacy: ptPrivacy,
    destinations: ptDestinations
  },
  el: {
    common: elCommon,
    calendar: elCalendar,
    explore: elExplore,
    howItWorks: elHowItWorks,
    incomingRequests: elIncomingRequests,
    corporate: elCorporate,
    terms: elTerms,
    privacy: elPrivacy,
    destinations: elDestinations
  },
  pl: {
    common: plCommon,
    calendar: plCalendar,
    explore: plExplore,
    howItWorks: plHowItWorks,
    incomingRequests: plIncomingRequests,
    corporate: plCorporate,
    terms: plTerms,
    privacy: plPrivacy,
    destinations: plDestinations
  }
};

const resources = {
  fr: baseResources.fr,
  en: baseResources.en,
  es: applyEnglishFallback(baseResources.es, baseResources.fr, baseResources.en),
  it: applyEnglishFallback(baseResources.it, baseResources.fr, baseResources.en),
  nl: applyEnglishFallback(baseResources.nl, baseResources.fr, baseResources.en),
  de: applyEnglishFallback(baseResources.de, baseResources.fr, baseResources.en),
  pt: applyEnglishFallback(baseResources.pt, baseResources.fr, baseResources.en),
  el: applyEnglishFallback(baseResources.el, baseResources.fr, baseResources.en),
  pl: applyEnglishFallback(baseResources.pl, baseResources.fr, baseResources.en),
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: 'fr',
    fallbackLng: (code) => {
      const normalized = (code || '').toLowerCase();
      if (normalized.startsWith('fr')) return ['fr', 'en'];
      return ['en'];
    },
    debug: false,
    resources: resources as any, // Bypass strict type check for generated fallback resources
    defaultNS: 'common',
    returnEmptyString: false,
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
